import { useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Start every page at the top.
 *
 * With one exception, and it is deliberately narrow: the params a screen writes
 * to remember its own view state. Manage Access keeps the selected role and
 * which groups are open in `?role=` and `?open=` so they survive a reload, and
 * without this, opening a group would throw the reader back to the top of a
 * long table every single time.
 *
 * Scoped to those two keys rather than to the navigation type. `REPLACE` looked
 * like the right signal and is not: pagination uses it too — `handlePageChange`
 * on Professionals, Prescriptions, Coupons, Forms, Submissions, Payment Pages,
 * Regions and Broadcast all call navigate(..., { replace: true }) — so skipping
 * every REPLACE silently stopped "next page" from returning to the top of the
 * list, on eleven screens that have nothing to do with access management.
 */
const VIEW_STATE_PARAMS = ["role", "open"];

export default function ScrollToTop() {
  const { pathname, search } = useLocation();
  const previous = useRef({ pathname, search });

  useLayoutEffect(() => {
    const before = previous.current;
    previous.current = { pathname, search };

    // A different page always starts at the top.
    if (before.pathname === pathname) {
      const changed = changedKeys(before.search, search);
      // Nothing but view-state params moved: the screen is rewriting its own
      // URL, not showing different content.
      if (
        changed.length > 0 &&
        changed.every((key) => VIEW_STATE_PARAMS.includes(key))
      ) {
        return;
      }
    }

    window.scrollTo(0, 0);
  }, [pathname, search]);

  return <></>;
}

/** Query-string keys whose value differs between the two, added or removed included. */
export function changedKeys(before: string, after: string): string[] {
  const a = new URLSearchParams(before);
  const b = new URLSearchParams(after);
  const keys = new Set([...a.keys(), ...b.keys()]);
  return [...keys].filter((key) => a.get(key) !== b.get(key));
}
